import { Download, GitFork, MessageSquarePlus, RefreshCw, Share2, SlidersHorizontal, Users, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HandoffChain, HandoffSuggestionRow } from "@/components/chat/AgentHandoff";
import { AgentPicker } from "@/components/chat/AgentPicker";
import { BrandPicker } from "@/components/chat/BrandPicker";
import { ChatHistorySidebar } from "@/components/chat/ChatHistorySidebar";
import { ChatInput } from "@/components/chat/ChatInput";
import { ChatMessageList } from "@/components/chat/ChatMessageList";
import { chatReasoningConfig } from "@/components/chat/chatReasoningConfig";
import { closeDocumentPreview, DocumentPreviewPanel } from "@/components/chat/DocumentPreviewModal";
import { DocumentVersionPanel } from "@/components/chat/DocumentVersionPanel";
import { closeDocumentVersionPanel } from "@/components/chat/documentVersionEvents";
import { ExportModal } from "@/components/chat/ExportModal";
import { ExtensionAccessModal } from "@/components/chat/ExtensionAccessModal";
import { closeHtmlPreview, HtmlPreviewPanel } from "@/components/chat/HtmlPreviewPanel";
import { MicroAppUpdateProvider, useMicroAppUpdateTarget } from "@/components/chat/MicroAppUpdateContext";
import { ModelSelector } from "@/components/chat/ModelSelector";
import { ProjectContextBar } from "@/components/chat/ProjectContextBar";
import { ReasoningEffortPicker } from "@/components/chat/ReasoningEffortPicker";
import { RouteDecisionRow } from "@/components/chat/RouteDecisionRow";
import { shouldDropEffort } from "@/components/chat/reasoningEffortGuard";
import { GoogleDriveBrowser } from "@/components/google-drive/GoogleDriveBrowser";
import { EntityShareDialog, TagPopover } from "@/components/shared";
import { useToast } from "@/components/shared/Toast";
import { SharePointBrowser } from "@/components/sharepoint/SharePointBrowser";
import { featureFlags } from "@/config/featureFlags";
import { getRuntimeConfig } from "@/config/runtime";
import { CHAT_ATTACHMENT_EXTENSIONS } from "@/constants/files";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useOperations } from "@/context/OperationContext";
import { useChatAttachments } from "@/hooks/useChatAttachments";
import { useChatExport } from "@/hooks/useChatExport";
import { useChatSession } from "@/hooks/useChatSession";
import { useChatSharing } from "@/hooks/useChatSharing";
import { useChatStreaming, visibleRouteState } from "@/hooks/useChatStreaming";
import { handoffChain } from "@/lib/agentHandoff";
import { chatService } from "@/lib/services";

function ChatInner() {
  const { sessionId, projectId: routeProjectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { enabledModels, tierDefaults } = useApp();
  const { profile } = useAuth();
  const { showToast } = useToast();
  const { findByEntityId } = useOperations();

  const chatSharing = useChatSharing();

  // Close all preview/version panels when navigating to a different session
  useEffect(() => {
    closeDocumentPreview();
    closeHtmlPreview();
    closeDocumentVersionPanel();
  }, [sessionId]);

  // Guard ref — updated after streaming/attachments hooks are initialized
  // so the session hook can check them without circular deps.
  const sessionGuardsRef = useRef({ isStreaming: false, isDownloadingFiles: false, askUserActive: false });

  // Session hook — owns all session/model/agent/message state + data loading.
  const session = useChatSession({
    sessionId,
    initialProjectUuid: routeProjectId || null,
    navigate,
    setSharedInfo: chatSharing.setSharedInfo,
    resetAttachments: () => attachments.reset(),
    disconnectStream: () => streaming.disconnectStream(),
    guardsRef: sessionGuardsRef,
  });

  const chatExport = useChatExport(session.currentSession);

  const attachments = useChatAttachments({
    modelSupportsVision: session.modelSupportsVision,
    inputValue: session.inputValue,
    setInputValue: session.setInputValue,
    allowedExtensions: CHAT_ATTACHMENT_EXTENSIONS,
  });

  const streaming = useChatStreaming({
    getMessages: session.getMessages,
    setMessages: session.setMessages,
    currentSession: session.currentSession,
    setCurrentSession: session.setCurrentSession,
    selectedModel: session.selectedModel,
    selectedAgent: session.selectedAgent,
    selectedBrand: session.selectedBrand,
    selectedReasoningEffort: session.selectedReasoningEffort,
    modelSupportsVision: session.modelSupportsVision,
    projectUuid: session.projectUuid,
    navigate,
    showToast,
    attachments,
    chatSharing,
    inputValue: session.inputValue,
    setInputValue: session.setInputValue,
    autoRoute: session.isAutoRoute,
    setSelectedAgent: session.setSelectedAgent,
    setAutoRoute: session.setAutoRoute,
    startNewChat: session.startNewChat,
  });

  // Keep session guard ref in sync with streaming/attachments state
  sessionGuardsRef.current = {
    isStreaming: streaming.isStreaming,
    isDownloadingFiles: attachments.isDownloadingFiles,
    askUserActive: !!(streaming.askUserQuestions && streaming.askUserQuestions.length > 0),
  };

  // Reconnect to an active operation if one exists for this chat session
  // (e.g. user navigated away and came back while chat is still streaming)
  useEffect(() => {
    const sessionUuid = session.currentSession?.uuid;
    if (!sessionUuid || streaming.isStreaming || streaming.operationId) return;
    const activeOp = findByEntityId(sessionUuid);
    if (activeOp) {
      streaming.reconnectToOperation(activeOp.operation_id);
    }
  }, [
    session.currentSession?.uuid,
    findByEntityId,
    streaming.isStreaming,
    streaming.operationId,
    streaming.reconnectToOperation,
  ]);

  // Export logic delegated to useChatExport hook (chatExport)
  // Streaming logic delegated to useChatStreaming hook (streaming)

  // Extract user-sent message content for up/down arrow history recall
  const sentMessages = useMemo(
    () => session.messages.filter((m) => m.role === "user").map((m) => m.content),
    [session.messages],
  );

  const routeState = visibleRouteState(streaming.routeState, session.currentSession, session.messages);
  const handoffs = useMemo(() => handoffChain(session.messages), [session.messages]);
  // An open chat can move to another agent (MHS-628): handoff on, the owner's own chat, between replies
  const canSwitchMidChat =
    featureFlags.agentHandoff &&
    !!session.currentSession &&
    !chatSharing.sharedInfo &&
    !streaming.isStreaming &&
    !streaming.askUserQuestions?.length;

  // Resolve slash commands for the currently selected agent
  const selectedAgentSlashCommands = useMemo(() => {
    if (!session.selectedAgent || !session.agents) return undefined;
    const agent = session.agents.find((a) => a.uuid === session.selectedAgent);
    return agent?.slashCommands;
  }, [session.selectedAgent, session.agents]);

  // When the selected agent pins its own model, the manual model picker should
  // be locked and reflect the agent's model to avoid a contradictory selection.
  const selectedAgentModel = useMemo(() => {
    if (!session.selectedAgent) return undefined;
    const pool = [...(session.agents ?? []), ...(session.nonProjectAgents ?? [])];
    const agent = pool.find((a) => a.uuid === session.selectedAgent);
    if (!agent?.modelId) return undefined;
    return {
      modelId: agent.modelId,
      label: agent.model?.displayName || agent.model?.name || agent.modelId,
    };
  }, [session.selectedAgent, session.agents, session.nonProjectAgents]);

  const activeReasoningConfig = useMemo(
    () => chatReasoningConfig(selectedAgentModel?.modelId, session.selectedModelInfo, enabledModels, tierDefaults),
    [selectedAgentModel, session.selectedModelInfo, enabledModels, tierDefaults],
  );

  const { selectedReasoningEffort, setReasoningEffort } = session;
  const hasSession = !!session.currentSession;
  useEffect(() => {
    if (shouldDropEffort(selectedReasoningEffort, activeReasoningConfig, hasSession)) {
      setReasoningEffort(null);
    }
  }, [selectedReasoningEffort, activeReasoningConfig, hasSession, setReasoningEffort]);

  const handleSessionDeleted = useCallback(
    (uuid: string) => {
      if (uuid === sessionId) {
        session.startNewChat(routeProjectId ?? session.projectUuid);
      }
    },
    [sessionId, routeProjectId, session],
  );

  // Consume prefill & agent from location state OR URL query params
  // (TeamHub and ProjectHub navigate with ?prefill=...&agent=...&project=...)
  const [searchParams] = useSearchParams();
  const prefillApplied = useRef(false);
  const autosubmitRequested = useRef(false);
  // Keep a stable ref to handleSend so the autosubmit effect can call it
  // without depending on the (unstable) streaming object.
  const handleSendRef = useRef(streaming.handleSend);
  handleSendRef.current = streaming.handleSend;

  // Project context is path-driven when nested under /projects/:projectId/chat,
  // with a query-string `?project=` fallback for any not-yet-migrated links.
  // Only relevant for a *fresh* chat (no sessionId yet) — once a session
  // exists, loadSession (in useChatSession) is the sole source of truth for
  // projectUuid, restoring it from the session's own persisted data. This
  // effect explicitly clears to null when neither route nor query project is
  // present, and re-evaluates whenever sessionId itself changes (not just
  // route/query), so navigating from an existing project-scoped session to a
  // brand new non-project chat (e.g. via the global "Chat" nav link, which
  // doesn't change the URL's project prefix because there wasn't one) can't
  // leave a stale project behind.
  useEffect(() => {
    if (sessionId) return;
    const queryProjectId = searchParams.get("project");
    const effectiveProjectId = routeProjectId || queryProjectId;
    session.setProjectUuid(effectiveProjectId || null);
  }, [sessionId, routeProjectId, searchParams, session.setProjectUuid]);

  // Canonical URL redirect: once a session loads and turns out to belong to
  // a project, make sure the URL reflects that (nested under /projects/:id/...)
  // even if it was reached via a plain /chat/:sessionId link (e.g. from the
  // chat history sidebar or a bookmark), so the project context bar always
  // shows up and the URL always matches reality.
  //
  // This reads projectUuid off `session.currentSession` (not the separate
  // `session.projectUuid` state) and requires currentSession.uuid to match
  // the route's sessionId. Those two fields are always set together in one
  // setCurrentSession() call inside useChatSession, so there's no window
  // where a redirect could fire using a previous session's stale project
  // while the new session is still loading (which `session.projectUuid`
  // alone can't guarantee, since it settles asynchronously).
  useEffect(() => {
    const loadedSession = session.currentSession;
    if (!sessionId || loadedSession?.uuid !== sessionId) return;
    const loadedProjectUuid = loadedSession.projectUuid || null;
    if (!loadedProjectUuid || loadedProjectUuid === routeProjectId) return;
    navigate(`/projects/${loadedProjectUuid}/chat/${sessionId}`, { replace: true });
  }, [sessionId, session.currentSession, routeProjectId, navigate]);

  useEffect(() => {
    if (sessionId || prefillApplied.current) return;

    // Try location.state first (e.g. from Deep Research "Send to Chat", or
    // ProjectHub's "Ask about this project" / agent cards)
    const state = location.state as { prefill?: string; agent?: string } | null;
    const statePrefill = state?.prefill;
    const paramPrefill = searchParams.get("prefill");
    const prefill = statePrefill || paramPrefill;
    const agentParam = state?.agent || searchParams.get("agent");
    const autosubmit = searchParams.get("autosubmit") === "1";

    if (prefill || agentParam) {
      prefillApplied.current = true;
      if (prefill) {
        // Only truncate URL-param prefills to prevent abuse; location.state
        // prefills (e.g. from Deep Research) can be arbitrarily long.
        session.setInputValue(statePrefill ? prefill : prefill.slice(0, 2000));
      }
      if (agentParam) {
        session.setSelectedAgent(agentParam);
      }
      // Clear query params / location state so it doesn't re-apply
      navigate(location.pathname, { replace: true, state: {} });

      // Auto-submit: schedule after a short delay so React state (inputValue,
      // selectedAgent) has settled. We use a ref for handleSend so this
      // timeout doesn't get cancelled by effect cleanup from unstable deps.
      if (autosubmit && prefill) {
        autosubmitRequested.current = true;
        setTimeout(() => {
          if (autosubmitRequested.current) {
            autosubmitRequested.current = false;
            handleSendRef.current();
          }
        }, 250);
      }
    }
  }, [location.state, searchParams, sessionId, session, navigate, location.pathname]);

  return (
    <div className="h-full flex overflow-hidden">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        {/* Header */}
        <div className="bg-surface-primary border-b border-border-primary px-4 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => session.startNewChat(routeProjectId ?? session.projectUuid)}
                className="flex items-center gap-2 px-4 py-2 bg-brand-gradient hover:opacity-90 text-white font-medium rounded-lg transition shadow-sm"
                title="Start a new chat"
              >
                <MessageSquarePlus className="w-4 h-4" />
                <span className="hidden sm:inline">New Chat</span>
              </button>

              <ModelSelector
                value={selectedAgentModel ? selectedAgentModel.modelId : session.selectedModel}
                onChange={(v) => session.setSelectedModel(v)}
                models={enabledModels}
                tierDefaults={tierDefaults}
                disabled={!!session.currentSession || !!selectedAgentModel}
                aria-label={selectedAgentModel ? `Model set by agent: ${selectedAgentModel.label}` : "Select model"}
              />

              <ReasoningEffortPicker
                config={activeReasoningConfig}
                value={session.selectedReasoningEffort}
                onChange={session.setReasoningEffort}
                disabled={!!chatSharing.sharedInfo}
              />

              <AgentPicker
                agents={session.agents ?? []}
                nonProjectAgents={session.nonProjectAgents ?? []}
                inProject={!!session.projectUuid}
                selectedAgentUuid={session.selectedAgent}
                onSelect={canSwitchMidChat ? streaming.selectAgentMidChat : session.selectAgent}
                auto={
                  !session.currentSession && session.autoRouteAvailable
                    ? { selected: session.isAutoRoute, onSelect: session.selectAutoRoute }
                    : undefined
                }
                noAgentOption={!session.currentSession}
                disabled={!!session.currentSession && !canSwitchMidChat}
              />
              <HandoffChain handoffs={handoffs} />

              {featureFlags.multiBrand && (
                <BrandPicker
                  selectedBrandUuid={session.selectedBrand}
                  onSelect={(uuid) => session.setSelectedBrand(uuid)}
                  disabled={!!session.currentSession}
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* AI Preferences quick-access */}
              {profile?.preferences?.responseTone ||
              profile?.preferences?.responseLength ||
              profile?.preferences?.customInstructions ? (
                <button
                  onClick={() => navigate("/settings?tab=ai_preferences")}
                  className="flex items-center gap-1.5 p-2 bg-brand-secondary-subtle hover:bg-brand-secondary/20 border border-brand-secondary/30 text-brand-secondary rounded-lg transition"
                  title={`Tone: ${profile.preferences.responseTone || "default"} | Length: ${profile.preferences.responseLength || "default"}${profile.preferences.customInstructions ? " | Custom instructions set" : ""}`}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={() => navigate("/settings?tab=ai_preferences")}
                  className="flex items-center gap-1.5 p-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary text-content-tertiary hover:text-content-primary rounded-lg transition"
                  title="Set AI response preferences"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
              )}

              {/* Share button - only show for owned sessions */}
              {session.currentSession && !chatSharing.sharedInfo && (
                <button
                  onClick={() => chatSharing.setIsShareDialogOpen(true)}
                  className="flex items-center gap-1.5 p-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary text-content-tertiary hover:text-content-primary rounded-lg transition"
                  title="Share this chat"
                >
                  <Share2 className="w-4 h-4" />
                </button>
              )}

              {/* Tags */}
              {session.currentSession && (
                <TagPopover entityType="chat_session" entityId={session.currentSession.uuid} mode="compact" />
              )}

              {/* Export button - only show when there's a session */}
              {session.currentSession && (
                <button
                  onClick={chatExport.openExportModal}
                  className="flex items-center gap-1.5 p-2 bg-surface-secondary hover:bg-interactive-active border border-border-secondary text-content-tertiary hover:text-content-primary rounded-lg transition"
                  title="Export this chat"
                >
                  <Download className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Project context bar — shown whenever this chat is scoped to a project */}
        {session.projectUuid && (
          <ProjectContextBar projectUuid={session.projectUuid} onLeave={() => session.startNewChat(null)} />
        )}

        {/* Shared session banner */}
        {chatSharing.sharedInfo && (
          <div className="bg-brand-secondary-subtle border-b border-brand-secondary/30 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-brand-secondary" />
              <span className="text-sm text-content-primary">
                Shared with you by <span className="font-medium">{chatSharing.sharedInfo.sharedBy.name}</span>
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-surface-secondary text-content-tertiary border border-border-secondary">
                {chatSharing.sharedInfo.permission === "read_write" ? "Read & Write" : "Read Only"}
              </span>
            </div>
            {chatSharing.sharedInfo.permission === "read_write" && (
              <div className="flex items-center gap-1.5 text-xs text-content-tertiary">
                <GitFork className="w-3.5 h-3.5" />
                <span>Sending a message will fork this chat</span>
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        <ChatMessageList
          messages={session.messages}
          isLoading={session.isLoading}
          streaming={{
            ...streaming,
            onAskUserSubmit: streaming.handleAskUserSubmit,
          }}
          isDownloadingFiles={attachments.isDownloadingFiles}
          sessionOwnerName={
            chatSharing.sharedInfo
              ? chatSharing.sharedInfo.sharedBy.name || session.currentSession?.userName
              : undefined
          }
          afterMessages={
            streaming.handoffSuggestion &&
            canSwitchMidChat && (
              <HandoffSuggestionRow
                suggestion={streaming.handoffSuggestion}
                onSwitch={streaming.acceptHandoffSuggestion}
                onDismiss={streaming.dismissHandoffSuggestion}
              />
            )
          }
          afterFirstUserMessage={
            routeState && (
              <RouteDecisionRow
                state={routeState}
                canSwitch={sentMessages.length === 1}
                agents={session.agents ?? []}
                nonProjectAgents={session.nonProjectAgents ?? []}
                inProject={!!session.projectUuid}
                onPickSuggestion={(uuid) => void streaming.resolveSuggestion(uuid)}
                onSwitch={streaming.switchRoutedAgent}
              />
            )
          }
        />

        {/* Input */}
        <ChatInput
          attachments={attachments}
          streaming={{ ...streaming, routePending: routeState?.kind === "suggest" }}
          session={{ ...session, sentMessages, slashCommands: selectedAgentSlashCommands, messages: session.messages }}
          chatSharing={chatSharing}
        />
      </div>

      {/* Share Dialog */}
      {session.currentSession && (
        <EntityShareDialog
          entityId={session.currentSession.uuid}
          entityTitle={session.currentSession.title}
          isOpen={chatSharing.isShareDialogOpen}
          onClose={() => chatSharing.setIsShareDialogOpen(false)}
          entityLabel="Chat"
          entityType="chat_session"
          shareLinkPath="/chat/shared/"
          service={chatService}
        />
      )}

      {/* Cloud Storage Browser Modal */}
      {attachments.isSharePointBrowserOpen && (
        <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden">
            {getRuntimeConfig().authProvider === "google" ? (
              <GoogleDriveBrowser
                onSelect={(selection) => {
                  attachments.setSharepointFiles((prev) => {
                    const existingIds = new Set(prev.map((f) => f.id));
                    const newFiles = selection.items
                      .filter((f) => !existingIds.has(f.id))
                      .map((f) => ({ id: f.id, name: f.name, web_url: f.web_url, is_folder: f.is_folder }));
                    return [...prev, ...newFiles];
                  });
                  attachments.setIsSharePointBrowserOpen(false);
                }}
                onCancel={() => attachments.setIsSharePointBrowserOpen(false)}
                multiSelect
                filesOnly
                title="Add Drive Files"
              />
            ) : (
              <SharePointBrowser
                onSelect={(selection) => {
                  attachments.setSharepointFiles((prev) => {
                    const existingIds = new Set(prev.map((f) => f.id));
                    const newFiles = selection.items.filter((f) => !existingIds.has(f.id));
                    return [...prev, ...newFiles];
                  });
                  attachments.setIsSharePointBrowserOpen(false);
                }}
                onCancel={() => attachments.setIsSharePointBrowserOpen(false)}
                multiSelect
                filesOnly
                allowOneDrive
                title="Add SharePoint Files"
              />
            )}
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal {...chatExport} />

      {/* Extension Access Modal */}
      {streaming.extensionAccessRequest && (
        <ExtensionAccessModal
          request={streaming.extensionAccessRequest}
          onClose={streaming.dismissExtensionAccess}
          onTurnedOn={streaming.handleExtensionAccessTurnedOn}
        />
      )}

      {/* Document Preview Panel - side-by-side with chat */}
      <DocumentPreviewPanel />

      {/* HTML Preview Panel - side-by-side with chat */}
      <HtmlPreviewPanel />

      {/* Document Version History Panel - side-by-side with chat */}
      <DocumentVersionPanel />

      {/* History Sidebar - Hidden on mobile (md breakpoint) */}
      <div className="hidden md:block">
        <ChatHistorySidebar
          currentSessionId={sessionId}
          refreshTrigger={streaming.sidebarRefreshTrigger}
          projectUuid={routeProjectId ?? session.projectUuid}
          onSessionDeleted={handleSessionDeleted}
        />
      </div>
    </div>
  );
}

/** Banner shown when the user is in a chat to update an existing micro app. */
function MicroAppUpdateBanner() {
  const { target, setTarget } = useMicroAppUpdateTarget();
  if (!target) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2 bg-brand-primary/10 border-b border-brand-primary/20 text-sm">
      <span className="inline-flex items-center gap-2 text-content-secondary min-w-0">
        <RefreshCw className="h-4 w-4 text-brand-primary shrink-0" />
        <span className="truncate">
          Updating <span className="font-medium text-content-primary">“{target.appName}”</span> — regenerate the
          dashboard, then use the <span className="font-medium text-brand-primary">Update</span> action on the artifact.
        </span>
      </span>
      <button
        type="button"
        onClick={() => setTarget(null)}
        aria-label="Cancel updating micro app"
        className="p-1 rounded hover:bg-brand-primary/10 text-content-tertiary transition shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function Chat() {
  const location = useLocation();
  const initialTarget = useMemo(() => {
    const state = location.state as { updateAppUuid?: string; updateAppName?: string } | null;
    if (state?.updateAppUuid) {
      return { appUuid: state.updateAppUuid, appName: state.updateAppName ?? "this app" };
    }
    return null;
    // Only seed from the navigation that mounted this page; ignore later state changes.
  }, []);

  return (
    <MicroAppUpdateProvider initialTarget={initialTarget}>
      <div className="h-full flex flex-col min-h-0">
        <MicroAppUpdateBanner />
        <div className="flex-1 min-h-0">
          <ChatInner />
        </div>
      </div>
    </MicroAppUpdateProvider>
  );
}
